import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Container,
  Button,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../utils/api';

const AllScholarships = () => {
  const navigate = useNavigate();
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchScholarships = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/schools/scholarships');
      setScholarships(response.data);
    } catch (error) {
      console.error('Error fetching scholarships:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScholarships();
  }, []);

  const renderItem = (item) => (
    <Grid item xs={12} sm={6} md={4} key={item._id}>
      <Card
        sx={{
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          borderRadius: 2,
          bgcolor: '#fff',
        }}
      >
        <CardActionArea
          onClick={() => navigate('/scholarship-details', { state: { scholarship: item } })}
        >
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 2 }}>
              {item.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BusinessIcon sx={{ color: '#6200ee', mr: 1, fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                {item.hostOrganization}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EventIcon sx={{ color: '#6200ee', mr: 1, fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                Deadline: {new Date(item.deadline).toLocaleDateString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SchoolIcon sx={{ color: '#6200ee', mr: 1, fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#666' }}>
                {item.level} • {item.type}
              </Typography>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f9fa',
        }}
      >
        <CircularProgress size={60} sx={{ color: '#6200ee' }} />
        <Typography variant="h6" sx={{ mt: 3, color: '#2c3e50' }}>
          Loading scholarships...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', mb: 3 }}>
          Scholarships
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/publish-scholarship')}
          sx={{
            bgcolor: '#6200ee',
            '&:hover': { bgcolor: '#7f39fb' },
            borderRadius: 2,
            mb: 4,
            px: 3,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 'bold',
          }}
        >
          Publish Scholarship
        </Button>
      </motion.div>
      {scholarships.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Typography sx={{ textAlign: 'center', color: '#666', mt: 5 }}>
            No scholarships published yet
          </Typography>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Grid container spacing={3}>
            {scholarships.map((item) => renderItem(item))}
          </Grid>
        </motion.div>
      )}
    </Container>
  );
};

export default AllScholarships;